import { useState, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material'
import { CloudUpload, Delete, Description, FolderOpen } from '@mui/icons-material'
import ReactMarkdown from 'react-markdown'
import api from '../api/client'
import { useAuthStore } from '../stores/authStore'
import { usePreferencesStore } from '../stores/preferencesStore'
import { usePortalStore } from '../stores/portalStore'
import UploadDialog, { type UploadItem } from '../components/UploadDialog'

const SUPPORTED_LANGS = ['es', 'en', 'fr', 'de', 'it', 'ca', 'eu']

export default function HelpPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role === 'admin'
  const language = usePreferencesStore((s) => s.language)
  const { config } = usePortalStore()
  const glassmorphism = (config?.glassmorphism ?? false) && config?.backgroundMode === 'image'
  const glassSx = glassmorphism
    ? { bgcolor: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }
    : {}

  const [uploadOpen, setUploadOpen] = useState(false)
  const [manageOpen, setManageOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)

  const helpLang = SUPPORTED_LANGS.includes(language) ? language : 'en'

  const { data: content, isLoading, error } = useQuery<string | null>({
    queryKey: ['help', helpLang],
    queryFn: async () => {
      for (const lang of [helpLang, 'en']) {
        try {
          const res = await api.get('/help/read', {
            params: { path: `help_${lang}.md` },
          })
          if (res.data?.content) return res.data.content
        } catch {
          // File not found, try next language
        }
      }
      return null
    },
    staleTime: 10 * 60 * 1000,
  })

  const { data: helpFiles } = useQuery<string[]>({
    queryKey: ['help-files'],
    queryFn: async () => {
      const res = await api.get('/help/list')
      return res.data?.files ?? []
    },
    enabled: isAdmin,
    staleTime: 10 * 60 * 1000,
  })

  const handleUpload = useCallback(async (items: UploadItem[]) => {
    const formData = new FormData()
    for (const item of items) {
      formData.append('files', item.file, item.relativePath || item.file.name)
    }
    await api.post('/help/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    queryClient.invalidateQueries({ queryKey: ['help'] })
    queryClient.invalidateQueries({ queryKey: ['help-files'] })
    setUploadOpen(false)
  }, [queryClient])

  const handleDelete = useCallback(async (filename: string) => {
    await api.post('/help/delete', null, { params: { filename } })
    queryClient.invalidateQueries({ queryKey: ['help'] })
    queryClient.invalidateQueries({ queryKey: ['help-files'] })
    setDeleteTarget(null)
  }, [queryClient])

  const markdownSx = {
    '& h1': { mt: 0, mb: 2 },
    '& h2': { mt: 4, mb: 1 },
    '& h3': { mt: 3, mb: 1 },
    '& p': { mb: 2, lineHeight: 1.7 },
    '& ul, & ol': { mb: 2, pl: 3 },
    '& li': { mb: 0.5 },
    '& code': {
      bgcolor: 'action.hover',
      px: 0.5,
      py: 0.2,
      borderRadius: 0.5,
      fontSize: '0.9em',
      fontFamily: 'monospace',
    },
    '& pre': {
      bgcolor: 'action.hover',
      p: 2,
      borderRadius: 1,
      overflow: 'auto',
      mb: 2,
    },
    '& a': { color: 'primary.main' },
    '& blockquote': {
      borderLeft: 3,
      borderColor: 'primary.main',
      pl: 2,
      ml: 0,
      mb: 2,
      fontStyle: 'italic',
    },
    '& img': { maxWidth: '100%', height: 'auto' },
    '& table': { width: '100%', display: 'block', overflowX: 'auto' },
    '& th, & td': { wordBreak: 'break-word' },
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" component="h2">
          {t('navigation.help')}
        </Typography>

        {isAdmin && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FolderOpen />}
              onClick={() => setManageOpen(true)}
              sx={glassSx}
            >
              {t('help.manageFiles')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudUpload />}
              onClick={() => setUploadOpen(true)}
              sx={glassSx}
            >
              {t('help.uploadFiles')}
            </Button>
          </Box>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{t('common.error')}</Alert>
      ) : content ? (
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            width: '100%',
            overflowX: 'auto',
            wordBreak: 'break-word',
            ...glassSx,
            ...markdownSx,
          }}
        >
          <ReactMarkdown>{content}</ReactMarkdown>
        </Box>
      ) : (
        <Alert severity="info">{t('help.noContent')}</Alert>
      )}

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={handleUpload}
      />

      {/* Admin: Manage Help Files dialog */}
      <Dialog open={manageOpen} onClose={() => setManageOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('help.manageFiles')}</DialogTitle>
        <DialogContent dividers>
          {!helpFiles?.length ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              {t('help.noFiles')}
            </Typography>
          ) : (
            <List dense>
              {helpFiles.map((name) => (
                <ListItem
                  key={name}
                  secondaryAction={
                    <Tooltip title={t('common.delete')}>
                      <IconButton edge="end" size="small" color="error" onClick={() => setDeleteTarget(name)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36 }}><Description fontSize="small" /></ListItemIcon>
                  <ListItemText primary={name} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>{t('help.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('help.confirmDeleteMsg', { file: deleteTarget })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t('common.cancel')}</Button>
          <Button color="error" onClick={() => deleteTarget && handleDelete(deleteTarget)}>{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}